import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDeviceCode } from '@pos/core';
import { discardRejectedOp, patchRow, rejectedOps, retryRejectedOps, type SyncRejectionRow } from '@pos/local-db';
import { SHARED_PUSH_TABLES, type OutboxOp, type SyncTable } from '@pos/sync';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { useOnline } from '../../lib/use-online.ts';
import { clearTimings, recentTimings, type Timing } from '../../lib/timings.ts';
import { requestSync, syncEngine, useSyncStatus } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, PageHeader, Spinner, errorMessage } from '../../ui/index.tsx';
import { useMoney } from '../products/data.ts';
import { useReport } from '../reports/data.ts';
import { describeChange, fieldLabel, formatFieldValue, recordName } from './describe.ts';
import {
  DAY_MS,
  applyRejection,
  fetchConflicts,
  fetchDeviceHealth,
  resolveConflict,
  testConnection,
  timeAgo,
  useOldestPendingAge,
  usePendingRejections,
  type ConnectionTest,
  type SyncConflict,
} from './data.ts';

const NO_CONFLICTS: SyncConflict[] = [];
const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });
const fmt = (iso: string | null | undefined) => (iso ? dateFormat.format(new Date(iso)) : '—');

/** Nombres de personas y dispositivos del negocio (bajados al dispositivo). */
function useNames(businessId: string) {
  return useLiveQuery(async () => {
    const [members, devices] = await Promise.all([
      db.memberships.where('business_id').equals(businessId).toArray(),
      db.devices.where('business_id').equals(businessId).toArray(),
    ]);
    return {
      person: (id: string | null | undefined) => members.find((m) => m.user_id === id)?.display_name ?? 'alguien',
      device: (id: string | null | undefined) => devices.find((d) => d.id === id)?.name ?? 'otro dispositivo',
    };
  }, [businessId]);
}

/** Estado de la sincronización: este dispositivo, lo rechazado y (para el dueño) todo el negocio. */
export function SyncPage() {
  const { isOwner } = useSession();
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader title="Sincronización" description="Qué falta enviar al servidor, qué no se aceptó y cómo están los dispositivos." />
      <ThisDevice />
      <RejectedHere />
      {isOwner && (
        <>
          <Quarantine />
          <Conflicts />
          <Devices />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Este dispositivo
// ---------------------------------------------------------------------------

function ThisDevice() {
  const { membership } = useSession();
  const online = useOnline();
  const status = useSyncStatus();
  const money = useMoney();
  const names = useNames(membership.business_id);
  const pending = useLiveQuery(() => db.outbox.where('status').equals('pending').sortBy('seq'), [], [] as OutboxOp[]);
  const oldestAge = useOldestPendingAge();
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    navigator.storage
      ?.persisted?.()
      .then(setPersisted)
      .catch(() => setPersisted(null));
  }, []);

  // Cambios de otra persona que solo se envían con su sesión (las ventas las envía cualquiera).
  const waitingFor = useMemo(() => {
    const others = new Map<string, number>();
    for (const op of pending) {
      if (op.created_by !== membership.user_id && !SHARED_PUSH_TABLES.includes(op.table as SyncTable)) {
        others.set(op.created_by, (others.get(op.created_by) ?? 0) + 1);
      }
    }
    return [...others];
  }, [pending, membership.user_id]);

  const visible = showAll ? pending : pending.slice(0, 8);

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Este dispositivo</h2>
          <p className="text-sm text-slate-600">
            {online ? 'Con conexión' : 'Sin conexión'} · Última sincronización: {status.lastSyncAt ? timeAgo(status.lastSyncAt) : 'todavía no en esta sesión'}
            {status.lastDurationMs != null && ` (tardó ${seconds(status.lastDurationMs)})`}
          </p>
        </div>
        <Button variant="secondary" size="sm" loading={status.syncing} disabled={!online} onClick={() => void syncEngine.sync()}>
          Sincronizar ahora
        </Button>
      </div>

      {status.lastError && online && <Alert tone="warning">No se pudo sincronizar: {status.lastError}. Se reintenta solo.</Alert>}
      {oldestAge != null && oldestAge > DAY_MS && (
        <Alert tone="error" title="Hay cambios de hace más de un día sin enviar">
          Hasta que se envíen, solo existen en este dispositivo. Conectalo a internet y dejá la app abierta unos minutos.
        </Alert>
      )}

      {pending.length === 0 ? (
        <p className="text-sm text-emerald-700">Todo enviado: no hay cambios pendientes.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {pending.length} {pending.length === 1 ? 'cambio sin enviar' : 'cambios sin enviar'}
          </p>
          {waitingFor.map(([userId, count]) => (
            <Alert key={userId} tone="info">
              {count} {count === 1 ? 'cambio de' : 'cambios de'} {names?.person(userId) ?? 'otra persona'} se {count === 1 ? 'envía' : 'envían'} cuando esa persona
              vuelva a entrar en este dispositivo con conexión.
            </Alert>
          ))}
          <ul className="divide-y divide-slate-100 rounded-lg text-sm ring-1 ring-slate-200">
            {visible.map((op) => (
              <li key={op.seq} className="flex flex-wrap items-baseline justify-between gap-x-3 px-3 py-2">
                <span>{describeChange({ table: op.table, kind: op.kind, row_id: op.row_id, payload: op.payload }, money)}</span>
                <span className="text-xs text-slate-500">
                  {fmt(op.created_at)}
                  {op.created_by !== membership.user_id && ` · ${names?.person(op.created_by) ?? ''}`}
                  {op.attempts > 0 && ` · ${op.attempts} ${op.attempts === 1 ? 'intento' : 'intentos'}`}
                </span>
              </li>
            ))}
          </ul>
          {pending.length > visible.length && (
            <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
              Ver los {pending.length}
            </Button>
          )}
        </div>
      )}

      <ConnectionCheck />

      {persisted === false && (
        <p className="text-xs text-slate-500">
          El navegador no garantiza guardar los datos de este sitio si se queda sin espacio. Instalá la app (menú del navegador → «Instalar») para protegerlos.
        </p>
      )}
    </Card>
  );
}

const seconds = (ms: number) => `${(ms / 1000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} s`;

/** Mide cuánto tarda el servidor en responder desde este dispositivo (para diagnosticar lentitud). */
function ConnectionCheck() {
  const { business } = useSession();
  const online = useOnline();
  const [result, setResult] = useState<ConnectionTest | null>(null);
  const [testing, setTesting] = useState(false);
  const [timings, setTimings] = useState<Timing[]>(recentTimings);
  const run = async () => {
    setTesting(true);
    try {
      setResult(await testConnection(business.id));
    } finally {
      setTesting(false);
      setTimings(recentTimings());
    }
  };
  const ms = (v: number | null) => (v === null ? 'falló' : seconds(v));
  const slow = result ? [result.server, ...result.queries].some((v) => v === null || v > 1500) : false;
  return (
    <div className="space-y-2 border-t border-slate-100 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">¿Anda lento? Medí cuánto tarda el servidor en responder desde este dispositivo.</p>
        <Button variant="secondary" size="sm" loading={testing} disabled={!online} onClick={run}>
          Probar conexión
        </Button>
      </div>
      {result && (
        <Alert tone={slow ? 'warning' : 'success'}>
          <p>
            Servidor: {ms(result.server)} · Consultas: {result.queries.map(ms).join(' / ')}
          </p>
          <p className="text-xs">
            {result.mode}
            {result.network && ` · red ${result.network}`}
            {result.error && ` · error: ${result.error}`}
          </p>
          <p className="mt-1 text-xs">{slow ? 'Más de 1,5 s por consulta es lento: probá con otra red (wifi o datos).' : 'Respuestas normales (menos de 1,5 s).'}</p>
        </Alert>
      )}
      {timings.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-600">Tiempos recientes en este dispositivo ({timings.length})</summary>
          <ul className="mt-2 divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {timings.slice(0, 25).map((t, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-x-3 px-3 py-1.5">
                <span>
                  {t.label}
                  {t.failed && <span className="text-red-700"> (falló)</span>}
                </span>
                <span className={t.ms > 2000 ? 'font-semibold text-red-700' : 'text-slate-500'}>
                  {seconds(t.ms)} · {timeAgo(t.at)}
                </span>
              </li>
            ))}
          </ul>
          <Button
            variant="ghost"
            size="sm"
            className="mt-1"
            onClick={() => {
              clearTimings();
              setTimings([]);
            }}
          >
            Borrar tiempos
          </Button>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rechazados en este dispositivo
// ---------------------------------------------------------------------------

function RejectedHere() {
  const { isOwner, writer } = useSession();
  const money = useMoney();
  const ops = useLiveQuery(() => rejectedOps(db), [], [] as OutboxOp[]);
  const quarantine = useLiveQuery(() => db.sync_rejections.toArray(), [], [] as SyncRejectionRow[]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (ops.length === 0) return null;
  const inReview = new Set(quarantine.filter((r) => r.status === 'pending').map((r) => r.id));

  const retry = async (seqs: number[]) => {
    setBusy(true);
    try {
      await retryRejectedOps(db, seqs);
      await syncEngine.sync();
    } finally {
      setBusy(false);
    }
  };

  const discard = async (op: OutboxOp) => {
    const warning =
      op.table === 'sales'
        ? 'La venta se borra de este dispositivo y NO va a llegar al servidor. ¿Seguro?'
        : op.kind === 'insert'
          ? 'Se borra de este dispositivo. ¿Seguro?'
          : 'Se descarta el cambio (queda lo que tiene el servidor). ¿Seguro?';
    if (!window.confirm(warning)) return;
    try {
      await discardRejectedOp(db, op.seq!, writer);
      requestSync(0);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold text-red-800">Cambios rechazados en este dispositivo</h2>
          <p className="text-sm text-slate-600">
            El servidor no los aceptó. Si el motivo ya se resolvió (te dieron el permiso, se actualizó el sistema), reintentalos.
            {!isOwner && ' Si no, el dueño los puede revisar y aplicar desde su cuenta, o los podés descartar.'}
          </p>
        </div>
        <Button size="sm" loading={busy} onClick={() => retry(ops.map((o) => o.seq!))}>
          Reintentar todos
        </Button>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <ul className="divide-y divide-slate-100 rounded-lg text-sm ring-1 ring-slate-200">
        {ops.map((op) => (
          <li key={op.seq} className="space-y-1 px-3 py-2">
            <p className="font-medium">{describeChange({ table: op.table, kind: op.kind, row_id: op.row_id, payload: op.payload }, money)}</p>
            <p className="text-red-700">{op.last_error ?? 'Rechazado'}</p>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-xs text-slate-500">
                {fmt(op.created_at)}
                {!isOwner && inReview.has(op.op_id) && <Badge tone="amber">En revisión del dueño</Badge>}
              </span>
              <span className="flex gap-1">
                <Button variant="dangerGhost" size="sm" onClick={() => discard(op)}>
                  Descartar
                </Button>
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => retry([op.seq!])}>
                  Reintentar
                </Button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dueño: cuarentena de todos los dispositivos
// ---------------------------------------------------------------------------

function Quarantine() {
  const { business, writer } = useSession();
  const online = useOnline();
  const money = useMoney();
  const names = useNames(business.id);
  const rows = usePendingRejections(business.id);
  // Los rechazados de este dispositivo ya se muestran arriba, con sus propias acciones.
  const localOps = useLiveQuery(async () => new Set((await db.outbox.toArray()).map((o) => o.op_id)), [], new Set<string>());
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const list = (rows ?? []).filter((r) => !localOps.has(r.id));
  if (list.length === 0) return null;

  const setError = (id: string, message: string | null) =>
    setErrors((e) => {
      const next = { ...e };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });

  const apply = async (row: SyncRejectionRow) => {
    setBusy(row.id);
    try {
      const rejected = await applyRejection(row.id);
      setError(row.id, rejected ? `Se volvió a rechazar: ${rejected}` : null);
      await syncEngine.sync();
    } catch (err) {
      setError(row.id, errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const discard = async (row: SyncRejectionRow) => {
    if (!window.confirm('El cambio no se va a aplicar y se borra también del dispositivo donde se hizo. ¿Seguro?')) return;
    try {
      await patchRow(db, 'sync_rejections', row.id, { status: 'discarded' }, writer);
      requestSync(0);
    } catch (err) {
      setError(row.id, errorMessage(err));
    }
  };

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Para revisar: cambios rechazados de otros dispositivos</h2>
        <p className="text-sm text-slate-600">
          Por ejemplo, un empleado sin permiso para cambiar precios que lo hizo sin conexión. «Aplicar» lo registra con tus permisos de dueño; «Descartar» lo
          deshace también en el dispositivo donde se hizo.
        </p>
      </div>
      <ul className="divide-y divide-slate-100 rounded-lg text-sm ring-1 ring-slate-200">
        {list.map((row) => (
          <li key={row.id} className="space-y-1 px-3 py-2">
            <p className="font-medium">{describeChange({ table: row.table_name, kind: row.kind, row_id: row.row_id, payload: row.payload }, money)}</p>
            <p className="text-xs text-slate-500">
              {names?.person(row.author_id)} · {names?.device(row.device_id)} · {fmt(row.op_created_at ?? row.rejected_at)}
            </p>
            <p className="text-red-700">Motivo: {row.error}</p>
            {errors[row.id] && <Alert tone="error">{errors[row.id]}</Alert>}
            <div className="flex justify-end gap-1">
              <Button variant="dangerGhost" size="sm" onClick={() => discard(row)}>
                Descartar
              </Button>
              <Button size="sm" loading={busy === row.id} disabled={!online || busy !== null} title={online ? undefined : 'Necesita conexión'} onClick={() => apply(row)}>
                Aplicar
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dueño: datos que cambiaron en dos lugares
// ---------------------------------------------------------------------------

function Conflicts() {
  const { business } = useSession();
  const [reload, setReload] = useState(0);
  const report = useReport((id) => fetchConflicts(id), [reload]);
  const conflicts = report.state === 'ready' ? report.data : NO_CONFLICTS;
  const names = useNames(business.id);
  const recordNames = useLiveQuery(
    async () => Promise.all(conflicts.map((c) => recordName(db, c.table_name, c.row_id))),
    [conflicts],
  );

  if (report.state === 'ready' && conflicts.length === 0) return null;
  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Datos cambiados en dos lugares a la vez</h2>
        <p className="text-sm text-slate-600">
          Cuando dos personas cambian el mismo dato (por ejemplo, un precio) y una estaba sin conexión, queda el cambio más reciente según la hora en que se
          hizo. Revisá que haya quedado bien.
        </p>
      </div>
      {report.state === 'loading' && <Spinner />}
      {report.state === 'offline' && <p className="text-sm text-slate-500">Se ve con conexión.</p>}
      {report.state === 'error' && <Alert tone="error">No se pudieron cargar: {report.message}</Alert>}
      {conflicts.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg text-sm ring-1 ring-slate-200">
          {conflicts.map((c, i) => (
            <ConflictItem key={c.id} conflict={c} name={recordNames?.[i] ?? ''} names={names} onDone={() => setReload((n) => n + 1)} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ConflictItem({
  conflict: c,
  name,
  names,
  onDone,
}: {
  conflict: SyncConflict;
  name: string;
  names: ReturnType<typeof useNames>;
  onDone: () => void;
}) {
  const money = useMoney();
  const online = useOnline();
  const [busy, setBusy] = useState<'keep' | 'revert' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const value = (v: unknown) => formatFieldValue(c.field, v, money);
  const late = `${value(c.incoming_value)} (${names?.person(c.user_id)}, ${names?.device(c.device_id)}, ${fmt(c.op_created_at)})`;
  const before = `${value(c.server_value)} (${c.server_changed_by ? names?.person(c.server_changed_by) : 'sin dato de quién'}${c.server_changed_at ? `, ${fmt(c.server_changed_at)}` : ''})`;
  const other = c.outcome === 'applied' ? c.server_value : c.incoming_value;

  const act = async (action: 'keep' | 'revert') => {
    setBusy(action);
    try {
      await resolveConflict(c.id, action);
      if (action === 'revert') await syncEngine.sync();
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(null);
    }
  };

  return (
    <li className="space-y-1 px-3 py-2">
      <p className="font-medium">
        {fieldLabel(c.field)} de {name}: quedó {value(c.outcome === 'applied' ? c.incoming_value : c.server_value)}
      </p>
      {c.outcome === 'applied' ? (
        <p className="text-slate-600">
          Llegó {late}, que reemplazó a {before}.
        </p>
      ) : (
        <p className="text-slate-600">
          Se mantuvo {before} porque era más reciente que {late}, que llegó después.
        </p>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      <div className="flex flex-wrap justify-end gap-1">
        <Button variant="secondary" size="sm" loading={busy === 'revert'} disabled={!online || busy !== null} onClick={() => act('revert')}>
          Cambiar a {value(other)}
        </Button>
        <Button size="sm" loading={busy === 'keep'} disabled={!online || busy !== null} onClick={() => act('keep')}>
          Está bien así
        </Button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Dueño: dispositivos
// ---------------------------------------------------------------------------

function Devices() {
  const { business, device: thisDevice } = useSession();
  const devices = useLiveQuery(
    () => db.devices.where('business_id').equals(business.id).filter((d) => !d.revoked_at).sortBy('code'),
    [business.id],
  );
  const health = useReport((id) => fetchDeviceHealth(id), []);

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Dispositivos</h2>
        <p className="text-sm text-slate-600">Cuándo sincronizó cada uno por última vez y si le quedan cambios sin enviar.</p>
      </div>
      {health.state === 'offline' && <p className="text-sm text-slate-500">Se ve con conexión.</p>}
      {health.state === 'error' && <Alert tone="error">No se pudo cargar: {health.message}</Alert>}
      <ul className="divide-y divide-slate-100 rounded-lg text-sm ring-1 ring-slate-200">
        {devices?.map((d) => {
          const h = health.state === 'ready' ? health.data.find((x) => x.device_id === d.id) : undefined;
          const staleSync = h?.last_sync_at ? Date.now() - new Date(h.last_sync_at).getTime() > 2 * DAY_MS : false;
          const stalePending = h?.oldest_pending_at ? Date.now() - new Date(h.oldest_pending_at).getTime() > DAY_MS : false;
          return (
            <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">{formatDeviceCode(d.code)}</span>
              <span className="min-w-0 flex-1 font-medium">
                {d.name}
                {d.id === thisDevice.id && <span className="font-normal text-slate-500"> (este)</span>}
              </span>
              {health.state === 'ready' && (
                <span className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  {h ? `Sincronizó ${timeAgo(h.last_sync_at)}` : 'Sin datos todavía'}
                  {h && h.pending_ops > 0 && <Badge tone={stalePending ? 'red' : 'amber'}>{h.pending_ops} sin enviar</Badge>}
                  {h && h.rejected_ops > 0 && <Badge tone="red">{h.rejected_ops} rechazados</Badge>}
                  {staleSync && <Badge tone="amber">Más de 2 días sin sincronizar</Badge>}
                  {h?.app_version && <span className="text-slate-400">v{h.app_version}</span>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
